import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { TemplateGroupModel } from '@models/templateGroup'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/templateGroup/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/templateGroup/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/templateGroup/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/templateGroup/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/templateGroup/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/templateGroup/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/templateGroup/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/templateGroup/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/templateGroup/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/templateGroup/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/templateGroup/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/templateGroup/DELETE_FAILURE'

export type TemplateGroupModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: TemplateGroupModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadTemplateGroups(options: EntityOptions = {}) {
  const { entityKey } = options

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult> => {
    const loadedForKeys = [...getState().templateGroups.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/template_groups.json', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_GROUP_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function loadTemplateGroupIds(templateGroupIds: number[], options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.templateGroups.loadedIds]
    const mergedIds = [...loadedIds, ...templateGroupIds]
    const uniqueIds = Array.from(new Set(mergedIds))

    const missingIds = templateGroupIds.filter(id => !loadedIds.includes(id))
    if (!missingIds.length){
      return new Promise((resolve) => {
        resolve({ success: true as const, data: [] })
      })
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds: uniqueIds }))

    const promise = api('/template_groups.json', { ...options, template_group_ids: JSON.stringify(missingIds) })
      .then(({ data }: { data: TemplateGroupModel[] }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_GROUP_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadTemplateGroup(templateGroupId: number, options: EntityOptions = {}) {
  // @ts-expect-error TS2314
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult> => {
    const state = getState()
    const loadedIds = [...state.templateGroups.loadedIds]

    if (templateGroupId && !loadedIds.includes(templateGroupId)){
      loadedIds.push(templateGroupId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/template_groups/${templateGroupId}.json`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createTemplateGroup(templateGroup: TemplateGroupModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      template_group: templateGroup,
    }),
  }

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch): Promise<ActionResult> => {
    dispatch(createRequest())

    const promise = api('/template_groups.json', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createTemplateGroupWithProject(
  templateGroup: Partial<TemplateGroupModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      template_group: templateGroup,
    }),
  }

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch): Promise<ActionResult> => {
    dispatch(createRequest())

    const promise = api('/template_groups/create_with_project.json', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateTemplateGroup(templateGroup: TemplateGroupModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      template_group: templateGroup,
    }),
  }

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch): Promise<ActionResult> => {
    dispatch(updateRequest())

    const promise = api(`/template_groups/${templateGroup.id}.json`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteTemplateGroup(templateGroup: TemplateGroupModel) {
  const config = {
    method: 'DELETE',
  }

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch): Promise<ActionResult> => {
    dispatch(deleteRequest())

    const promise = api(`/template_groups/${templateGroup.id}.json`, {}, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_GROUP)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: TemplateGroupModuleState = initialState,
  action: Action = { type: '' },
): TemplateGroupModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds ? Array.from(new Set([...state.loadedIds, ...action.loadedIds])) : state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loadedIds: action.loadedIds ? Array.from(new Set([...state.loadedIds, ...action.loadedIds])) : state.loadedIds,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
